package com.schoolconnect.app.feature.calendar

import com.schoolconnect.app.datasource.network.response.CalendarEventPublicResponse
import com.schoolconnect.app.datasource.network.response.CalendarEventAdminResponse
import com.schoolconnect.app.datasource.network.response.EventResponse
import com.schoolconnect.app.datasource.network.request.CalendarEventBulkCreateRequest
import com.schoolconnect.app.datasource.network.request.CalendarEventCreateRequest
import com.schoolconnect.app.datasource.network.request.CalendarEventUpdateRequest
import com.schoolconnect.app.feature.attendance.ParentAttendanceChild
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.designsystem.AppToastCenter
import com.schoolconnect.app.feature.common.ParentChildSelectionStore
import com.schoolconnect.app.repository.calendar.CalendarRepository
import com.schoolconnect.app.repository.eventsnews.EventsNewsRepository
import com.schoolconnect.app.repository.parentevents.ParentEventsRepository
import com.schoolconnect.app.repository.parentevents.ParentCalendarEvent
import com.schoolconnect.app.repository.parentevents.ParentCalendarEventDetail
import com.schoolconnect.app.repository.user.UserRepository
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class CalendarViewModel(
    private val academicYearId: String = "",
    children: List<ParentAttendanceChild> = emptyList(),
    private val useTeacherEventsApi: Boolean = false,
    private val useParentEventsApi: Boolean = false
) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private var loadJob: Job? = null
    private var saveJob: Job? = null
    private val _uiState = MutableStateFlow(CalendarUiState(children = children))
    val uiState: StateFlow<CalendarUiState> = _uiState.asStateFlow()

    init { observeGlobalSelection(); loadEvents() }

    fun onEvent(event: CalendarUiEvent) {
        when (event) {
            CalendarUiEvent.BackClicked -> Unit
            CalendarUiEvent.RefreshClicked -> loadEvents()
            is CalendarUiEvent.SearchChanged -> { _uiState.update { it.copy(searchQuery = event.query, pageIndex = 0) }; loadEvents(true) }
            is CalendarUiEvent.MonthChanged -> { _uiState.update { it.copy(month = event.month.ifBlank { currentCalendarMonthLabel() }, pageIndex = 0) }; loadEvents() }
            is CalendarUiEvent.ListMonthChanged -> { _uiState.update { it.copy(listMonthFilter = event.month, pageIndex = 0) }; loadEvents() }
            is CalendarUiEvent.EventTypeChanged -> { _uiState.update { it.copy(eventType = event.eventType, pageIndex = 0) }; loadEvents() }
            is CalendarUiEvent.StatusChanged -> { _uiState.update { it.copy(statusFilter = event.status, pageIndex = 0) }; loadEvents() }
            CalendarUiEvent.FiltersCleared -> { _uiState.update { it.copy(searchQuery = "", listMonthFilter = "", eventType = "", statusFilter = "", pageIndex = 0) }; loadEvents() }
            is CalendarUiEvent.AddEventsSubmitted -> saveEvents(event.events, event.publish)
            is CalendarUiEvent.PublishRequested -> publishEvent(event.id)
            is CalendarUiEvent.DeleteRequested -> _uiState.update { state ->
                state.copy(selectedId = "", deletingEvent = state.items.firstOrNull { it.id == event.id })
            }
            CalendarUiEvent.DeleteDismissed -> _uiState.update { it.copy(deletingEvent = null) }
            CalendarUiEvent.DeleteConfirmed -> confirmDeleteEvent()
            is CalendarUiEvent.SortChanged -> { _uiState.update { it.copy(sortField = event.field, sortAscending = if (it.sortField == event.field) !it.sortAscending else true, pageIndex = 0) }; loadEvents() }
            is CalendarUiEvent.PageChanged -> { _uiState.update { it.copy(pageIndex = event.index.coerceAtLeast(0)) }; loadEvents() }
            is CalendarUiEvent.PageSizeChanged -> { _uiState.update { it.copy(pageSize = event.size.coerceIn(PaginationPolicy.PAGE_SIZE_OPTIONS.min(), PaginationPolicy.PAGE_SIZE_OPTIONS.max()), pageIndex = 0) }; loadEvents() }
            CalendarUiEvent.ListViewSelected -> { _uiState.update { it.copy(listView = true, pageIndex = 0) }; loadEvents() }
            CalendarUiEvent.CalendarViewSelected -> { _uiState.update { it.copy(listView = false, pageIndex = 0) }; loadEvents() }
            is CalendarUiEvent.RowActionsToggled -> _uiState.update { it.copy(selectedId = if (it.selectedId == event.id) "" else event.id) }
            CalendarUiEvent.RowActionsDismissed -> _uiState.update { it.copy(selectedId = "") }
            is CalendarUiEvent.EditRequested -> _uiState.update { it.copy(selectedId = "", editingEvent = it.items.firstOrNull { row -> row.id == event.id }) }
            CalendarUiEvent.EditDismissed -> _uiState.update { it.copy(editingEvent = null) }
            is CalendarUiEvent.EditSaved -> updateEvent(event.event)
            is CalendarUiEvent.ViewRequested -> viewEvent(event.id)
            CalendarUiEvent.ViewDismissed -> _uiState.update { it.copy(viewingEvent = null) }
            is CalendarUiEvent.ChildSelected -> _uiState.update { state -> state.copy(selectedChildIndex = event.index.coerceIn(0, state.children.lastIndex.coerceAtLeast(0))) }
            is CalendarUiEvent.ImportFileSelected -> {
                if (event.fileBytes.isEmpty()) {
                    _uiState.update {
                        it.copy(importFileName = event.fileName, importFileBytes = null, importValidation = null, importError = "The selected workbook is empty.", importIsLoading = false, importSuccess = "")
                    }
                } else {
                    _uiState.update { it.copy(importFileName = event.fileName, importFileBytes = event.fileBytes, importValidation = null, importError = "", importSuccess = "") }
                    validateImport()
                }
            }
            is CalendarUiEvent.ImportFileReadFailed -> _uiState.update {
                it.copy(importFileName = "", importFileBytes = null, importValidation = null, importError = event.message, importIsLoading = false)
            }
            is CalendarUiEvent.ImportCommitRequested -> commitImport(event.publish)
            CalendarUiEvent.ImportReset -> _uiState.update { it.copy(importFileName = "", importFileBytes = null, importValidation = null, importError = "", importSuccess = "") }
            CalendarUiEvent.ImportTemplateRequested -> downloadImportTemplate()
            CalendarUiEvent.ImportCurrentDataRequested -> downloadCurrentData()
        }
    }

    private fun validateImport() {
        val state = _uiState.value
        val bytes = state.importFileBytes ?: return
        saveJob?.cancel()
        saveJob = scope.launch {
            _uiState.update { it.copy(importIsLoading = true, importError = "", importSuccess = "") }
            CalendarRepository.validateImport(state.importFileName, bytes).fold(
                onSuccess = { response ->
                    val errors = response.errors.orEmpty().map { row ->
                        CalendarImportErrorUi(
                            rowNumber = row.rowNumber?.toString().orEmpty(), date = row.eventDate.orEmpty(),
                            title = row.eventTitle.orEmpty(), message = row.errors.orEmpty().joinToString("; ")
                        )
                    }
                    val preview = response.validPayload.orEmpty().map { payload ->
                        fun field(name: String): String = payload[name]?.toString()?.trim('"')
                            ?.takeUnless { it.equals("null", ignoreCase = true) }
                            .orEmpty()
                        CalendarImportPreviewUi(
                            date = field("event_date").substringBefore('T').toDisplayDate(),
                            from = field("from_time").toDisplayTime().ifBlank { "All Day" },
                            to = field("to_time").toDisplayTime().ifBlank { "—" },
                            title = field("event_title"),
                            type = field("event_type")
                        )
                    }
                    val invalidRows = response.invalidRows ?: 0
                    _uiState.update { it.copy(importIsLoading = false, importValidation = CalendarImportValidationUi(response.sessionId.orEmpty(), response.fileName ?: state.importFileName, response.totalRows ?: 0, response.validRows ?: 0, invalidRows, preview, errors)) }
                    if (invalidRows == 0 && (response.validRows ?: 0) > 0) {
                        AppToastCenter.showSuccess("Calendar workbook validated successfully")
                    }
                },
                onFailure = { error -> _uiState.update { it.copy(importIsLoading = false, importError = error.message.orEmpty().ifBlank { "Unable to validate calendar import." }) } }
            )
        }
    }

    private fun commitImport(publish: Boolean) {
        val sessionId = _uiState.value.importValidation?.sessionId.orEmpty()
        if (sessionId.isBlank()) return
        saveJob?.cancel()
        saveJob = scope.launch {
            _uiState.update { it.copy(importIsLoading = true, importError = "", importSuccess = "") }
            CalendarRepository.commitImport(sessionId, publish).fold(
                onSuccess = { result ->
                    val imported = result.importedCount ?: 0
                    val skipped = result.skippedCount ?: 0
                    val disposition = if (publish) "published" else "saved as drafts"
                    val serverMessage = result.message.orEmpty().trim()
                    val outcome = buildList {
                        if (imported > 0) add("$imported calendar event${if (imported == 1) "" else "s"} $disposition.")
                        if (skipped > 0) add("$skipped duplicate or invalid row${if (skipped == 1) "" else "s"} skipped.")
                        result.errors.orEmpty().filter(String::isNotBlank).forEach(::add)
                        if (isEmpty() && serverMessage.isNotBlank()) add(serverMessage)
                        if (isEmpty()) add("Import request completed. The server reported 0 imported events.")
                    }.joinToString(" ")
                    val message = outcome.ifBlank { serverMessage.ifBlank { "Calendar import completed." } }
                    _uiState.update { it.copy(importIsLoading = false, importSuccess = message, saveGeneration = it.saveGeneration + 1, importCompletionGeneration = it.importCompletionGeneration + 1) }
                    AppToastCenter.showSuccess(message)
                    loadEvents()
                },
                onFailure = { error -> _uiState.update { it.copy(importIsLoading = false, importError = error.message.orEmpty().ifBlank { "Unable to import calendar." }) } }
            )
        }
    }

    private fun downloadImportTemplate() {
        saveJob?.cancel()
        saveJob = scope.launch {
            _uiState.update { it.copy(importIsLoading = true, importError = "") }
            CalendarRepository.downloadImportTemplate().fold(
                onSuccess = { bytes ->
                    _uiState.update { it.copy(importIsLoading = false, importSuccess = "Calendar import template downloaded.") }
                    AppToastCenter.showSuccess("Calendar import template downloaded.")
                },
                onFailure = { error -> _uiState.update { it.copy(importIsLoading = false, importError = error.message.orEmpty().ifBlank { "Unable to download import template." }) } }
            )
        }
    }

    private fun downloadCurrentData() {
        saveJob?.cancel()
        saveJob = scope.launch {
            _uiState.update { it.copy(importIsLoading = true, importError = "", importSuccess = "") }
            CalendarRepository.downloadCurrentData().fold(
                onSuccess = {
                    val message = "Current calendar data downloaded."
                    _uiState.update { it.copy(importIsLoading = false, importSuccess = message) }
                    AppToastCenter.showSuccess(message)
                },
                onFailure = { error -> _uiState.update { it.copy(importIsLoading = false, importError = error.message.orEmpty().ifBlank { "Unable to download current calendar data." }) } }
            )
        }
    }

    private fun saveEvents(events: List<CalendarEventInputUi>, publish: Boolean) {
        if (events.isEmpty()) return
        val requests = events.mapNotNull { input ->
            val date = input.date.toApiDate() ?: return@mapNotNull null
            CalendarEventCreateRequest(
                eventDate = date, isAllDay = input.allDay,
                fromTime = input.from.toApiTime(), toTime = input.to.toApiTime(),
                eventTitle = input.title.trim(), eventType = input.eventType.trim(),
                status = if (publish) "PUBLISHED" else "DRAFT"
            )
        }
        if (requests.size != events.size || requests.any { it.eventTitle.isBlank() || it.eventType.isBlank() }) return
        saveJob?.cancel()
        saveJob = scope.launch {
            _uiState.update { it.copy(isSaving = true, errorMessage = "") }
            CalendarRepository.createEvent(CalendarEventBulkCreateRequest(requests, if (publish) "PUBLISHED" else "DRAFT")).fold(
                onSuccess = {
                    _uiState.update { it.copy(isSaving = false, saveGeneration = it.saveGeneration + 1) }
                    AppToastCenter.showSuccess(
                        if (publish) "Calendar event published successfully" else "Calendar event saved as draft successfully"
                    )
                    loadEvents()
                },
                onFailure = { error -> _uiState.update { it.copy(isSaving = false, errorMessage = error.message.orEmpty().ifBlank { "Unable to save calendar events." }) } }
            )
        }
    }

    private fun publishEvent(eventId: String) {
        val normalizedId = eventId.trim()
        if (normalizedId.isBlank()) return
        saveJob?.cancel()
        saveJob = scope.launch {
            _uiState.update { it.copy(isSaving = true, errorMessage = "") }
            CalendarRepository.updateEvent(normalizedId, com.schoolconnect.app.datasource.network.request.CalendarEventUpdateRequest(status = "PUBLISHED")).fold(
                onSuccess = { _uiState.update { it.copy(isSaving = false) }; loadEvents() },
                onFailure = { error -> _uiState.update { it.copy(isSaving = false, errorMessage = error.message.orEmpty().ifBlank { "Unable to publish calendar event." }) } }
            )
        }
    }

    fun confirmDeleteEvent() {
        val eventId = _uiState.value.deletingEvent?.id?.trim().orEmpty()
        if (eventId.isBlank()) return
        saveJob?.cancel()
        saveJob = scope.launch {
            _uiState.update { it.copy(isSaving = true, deletingEvent = null, errorMessage = "") }
            CalendarRepository.deleteEvent(eventId).fold(
                onSuccess = {
                    _uiState.update { it.copy(isSaving = false, saveGeneration = it.saveGeneration + 1) }
                    loadEvents()
                },
                onFailure = { error ->
                    _uiState.update { it.copy(isSaving = false, errorMessage = error.message.orEmpty().ifBlank { "Unable to delete calendar event." }) }
                }
            )
        }
    }

    private fun updateEvent(event: CalendarRowUi) {
        val normalizedId = event.id.trim()
        val date = event.date.toApiDate()
        if (normalizedId.isBlank() || date == null || event.title.isBlank() || event.eventType.isBlank() || event.status.isBlank()) return
        saveJob?.cancel()
        saveJob = scope.launch {
            _uiState.update { it.copy(isSaving = true, errorMessage = "") }
            CalendarRepository.updateEvent(
                normalizedId,
                CalendarEventUpdateRequest(
                    eventDate = date,
                    isAllDay = event.isAllDay,
                    fromTime = if (event.isAllDay) null else event.from.toApiTime(),
                    toTime = if (event.isAllDay) null else event.to.toApiTime(),
                    eventTitle = event.title.trim(),
                    eventType = event.eventType.trim(),
                    status = event.status.trim().uppercase()
                )
            ).fold(
                onSuccess = {
                    _uiState.update { it.copy(isSaving = false, editingEvent = null) }
                    loadEvents()
                },
                onFailure = { error ->
                    _uiState.update { it.copy(isSaving = false, errorMessage = error.message.orEmpty().ifBlank { "Unable to update calendar event." }) }
                }
            )
        }
    }

    private fun viewEvent(eventId: String) {
        val row = _uiState.value.items.firstOrNull { it.id == eventId } ?: return
        _uiState.update { it.copy(selectedId = "", viewingEvent = row) }
        scope.launch {
            val detailResult = when {
                useParentEventsApi -> ParentEventsRepository.loadCalendarDetail(eventId).map { detail -> detail.toCalendarUi(row) }
                useTeacherEventsApi -> EventsNewsRepository.loadDetail(eventId).map { detail -> detail.toCalendarUi(row) }
                else -> CalendarRepository.getEvent(eventId).map { detail -> detail.toUi(row) }
            }
            detailResult.fold(
                onSuccess = { detail ->
                    val createdBy = if (useParentEventsApi || useTeacherEventsApi) detail.createdBy else {
                        detail.createdBy.takeIf { it.isNotBlank() }?.let { UserRepository.loadUserDisplayName(it).getOrNull().orEmpty() }.orEmpty()
                    }
                    val updatedBy = if (useParentEventsApi || useTeacherEventsApi) detail.lastUpdatedBy else {
                        detail.lastUpdatedBy.takeIf { it.isNotBlank() }?.let { UserRepository.loadUserDisplayName(it).getOrNull().orEmpty() }.orEmpty()
                    }
                    _uiState.update {
                        it.copy(
                            viewingEvent = detail.copy(
                                createdBy = createdBy.ifBlank { detail.createdBy },
                                lastUpdatedBy = updatedBy.ifBlank { detail.lastUpdatedBy }
                            )
                        )
                    }
                },
                onFailure = { error ->
                    scLog("[DATA_RETRIEVE_SC] CalendarViewModel viewEvent failure source=${when { useParentEventsApi -> "parent"; useTeacherEventsApi -> "teacher"; else -> "calendar" }} eventId=$eventId", error)
                    _uiState.update { it.copy(viewingEvent = null, errorMessage = error.message.orEmpty().ifBlank { "Unable to load calendar event details." }) }
                }
            )
        }
    }

    private fun observeGlobalSelection() {
        scope.launch { ParentChildSelectionStore.selectedChildId.collect { childId ->
            val index = _uiState.value.children.indexOfFirst { it.id == childId }
            if (index >= 0 && index != _uiState.value.selectedChildIndex) onEvent(CalendarUiEvent.ChildSelected(index))
        } }
    }

    private fun loadEvents(debounce: Boolean = false) {
        loadJob?.cancel()
        loadJob = scope.launch {
            if (debounce) delay(300)
            val state = _uiState.value
            val range = if (state.listView) monthRange(state.listMonthFilter) else monthRange(state.month)
            _uiState.update { it.copy(isLoading = true, errorMessage = "") }
            val result = if (useParentEventsApi) {
                ParentEventsRepository.loadForCalendar(refreshScope = scope).map { events -> events.map(ParentCalendarEvent::toCalendarUi) to events.size }
            } else if (useTeacherEventsApi) {
                EventsNewsRepository.loadPage(
                    pageIndex = 0,
                    pageSize = 100,
                    status = state.statusFilter.takeIf { it.isNotBlank() },
                    sortBy = state.sortField.toTeacherApiField(),
                    order = if (state.sortAscending) "asc" else "desc",
                    refreshScope = scope
                ).map { page -> page.items.map(EventResponse::toCalendarUi) to page.totalCount }
            } else {
                CalendarRepository.loadEvents(
                    pageIndex = state.pageIndex, pageSize = state.pageSize,
                    status = state.statusFilter.takeIf { it.isNotBlank() }, eventType = state.eventType.takeIf { it.isNotBlank() },
                    academicYearId = academicYearId.takeIf { it.isNotBlank() }, fromDate = range.first, toDate = range.second,
                    sortBy = state.sortField.toApiField(), order = if (state.sortAscending) "asc" else "desc", refreshScope = scope
                ).map { page -> page.items.map(CalendarEventPublicResponse::toUi) to page.totalCount }
            }
            result.fold(
                onSuccess = { (loadedRows, serverTotal) ->
                    val query = state.searchQuery.trim().lowercase()
                    val rows = loadedRows.filter { row ->
                        val fromDate = range.first
                        val toDate = range.second
                        val inMonth = if ((useParentEventsApi || useTeacherEventsApi) && fromDate != null && toDate != null) {
                            row.date.toApiDate()?.let { it >= fromDate && it <= toDate } != false
                        } else true
                        inMonth && (query.isBlank() || listOf(row.date, row.title, row.eventType, row.status).any { it.lowercase().contains(query) }) &&
                            (state.eventType.isBlank() || row.eventType == state.eventType)
                    }.let { rows ->
                        if (state.sortField == CalendarSortField.FROM || state.sortField == CalendarSortField.TO) {
                            val selector: (CalendarRowUi) -> String = if (state.sortField == CalendarSortField.FROM) {
                                { row -> row.from.toSortTime() }
                            } else {
                                { row -> row.to.toSortTime() }
                            }
                            rows.sortedWith(compareBy(selector).let { comparator -> if (state.sortAscending) comparator else comparator.reversed() })
                        } else rows
                    }
                    _uiState.update { it.copy(items = rows, totalCount = if (useParentEventsApi || useTeacherEventsApi || query.isNotBlank()) rows.size else serverTotal, isLoading = false) }
                },
                onFailure = { error -> _uiState.update { it.copy(items = emptyList(), totalCount = 0, isLoading = false, errorMessage = error.message.orEmpty().ifBlank { "Unable to load calendar events." }) } }
            )
        }
    }

    fun clear() { scope.cancel() }
}

private fun CalendarSortField.toApiField() = when (this) {
    CalendarSortField.DATE -> "event_date"
    CalendarSortField.FROM, CalendarSortField.TO -> null
    CalendarSortField.TITLE -> "event_title"
    CalendarSortField.EVENT_TYPE -> "event_type"
    CalendarSortField.STATUS -> "status"
}

private fun CalendarSortField.toTeacherApiField() = when (this) {
    CalendarSortField.DATE -> "event_date"
    CalendarSortField.TITLE -> "title"
    CalendarSortField.STATUS -> "status"
    CalendarSortField.FROM, CalendarSortField.TO, CalendarSortField.EVENT_TYPE -> null
}

private fun String.toSortTime(): String = when {
    equals("All Day", ignoreCase = true) || this == "—" -> ""
    else -> trim()
}

private fun monthRange(value: String): Pair<String?, String?> {
    val parts = value.trim().split(Regex("\\s+"))
    if (parts.size != 2) return null to null
    val month = listOf("january", "february", "march", "april", "may", "june", "july", "august", "september", "october", "november", "december").indexOf(parts[0].lowercase()) + 1
    val year = parts[1].toIntOrNull() ?: return null to null
    if (month !in 1..12) return null to null
    val lastDay = listOf(31, if (year % 4 == 0) 29 else 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31)[month - 1]
    return "$year-${month.toString().padStart(2, '0')}-01" to "$year-${month.toString().padStart(2, '0')}-$lastDay"
}

private fun CalendarEventPublicResponse.toUi() = CalendarRowUi(
    id = id.orEmpty(), date = eventDate.orEmpty().toDisplayDate(), from = if (isAllDay == true) "All Day" else fromTime.orEmpty().toDisplayTime(),
    to = if (isAllDay == true) "—" else toTime.orEmpty().toDisplayTime(), title = eventTitle.orEmpty().ifBlank { "Untitled event" },
    eventType = eventType.orEmpty(), status = status.orEmpty(), isAllDay = isAllDay == true
)

private fun EventResponse.toCalendarUi() = CalendarRowUi(
    id = id.orEmpty().ifBlank { eventId.orEmpty() },
    date = eventDate.orEmpty().substringBefore('T').toDisplayDate(),
    title = title.orEmpty().ifBlank { "Untitled event" },
    eventType = audience.orEmpty().ifBlank { "Other" },
    status = status.orEmpty().ifBlank { if (isActive == true) "PUBLISHED" else "DRAFT" },
    createdOn = createdAt.orEmpty(),
    lastUpdatedOn = updatedAt.orEmpty()
)

private fun com.schoolconnect.app.repository.parentevents.ParentCalendarEvent.toCalendarUi() = CalendarRowUi(
    id = id,
    date = date.substringBefore('T').toDisplayDate(),
    title = title,
    eventType = eventType,
    status = status,
    createdOn = createdOn.orEmpty(),
    createdBy = createdBy.orEmpty(),
    lastUpdatedOn = lastUpdatedOn.orEmpty().ifBlank { createdOn.orEmpty() },
    lastUpdatedBy = lastUpdatedBy.orEmpty().ifBlank { createdBy.orEmpty() }
)

private fun ParentCalendarEventDetail.toCalendarUi(fallback: CalendarRowUi) = fallback.copy(
    date = date?.substringBefore('T')?.toDisplayDate() ?: fallback.date,
    title = title ?: fallback.title,
    eventType = eventType ?: fallback.eventType,
    status = status ?: fallback.status,
    createdOn = createdOn ?: fallback.createdOn,
    createdBy = createdBy ?: fallback.createdBy,
    lastUpdatedOn = lastUpdatedOn ?: fallback.lastUpdatedOn,
    lastUpdatedBy = lastUpdatedBy ?: fallback.lastUpdatedBy
)

private fun EventResponse.toCalendarUi(fallback: CalendarRowUi) = CalendarRowUi(
    id = id.orEmpty().ifBlank { fallback.id },
    date = eventDate?.substringBefore('T')?.toDisplayDate() ?: fallback.date,
    title = title.orEmpty().ifBlank { fallback.title },
    eventType = audience.orEmpty().ifBlank { fallback.eventType },
    status = status.orEmpty().ifBlank { fallback.status },
    createdOn = createdAt.orEmpty().ifBlank { fallback.createdOn },
    lastUpdatedOn = updatedAt.orEmpty().ifBlank { createdAt.orEmpty().ifBlank { fallback.lastUpdatedOn } },
    lastUpdatedBy = fallback.lastUpdatedBy.ifBlank { fallback.createdBy }
)

private fun CalendarEventAdminResponse.toUi(fallback: CalendarRowUi) = CalendarRowUi(
    id = id ?: fallback.id,
    date = eventDate?.toDisplayDate() ?: fallback.date,
    from = if (isAllDay == true) "All Day" else fromTime?.toDisplayTime() ?: fallback.from,
    to = if (isAllDay == true) "—" else toTime?.toDisplayTime() ?: fallback.to,
    title = eventTitle ?: fallback.title,
    eventType = eventType ?: fallback.eventType,
    status = status ?: fallback.status,
    isAllDay = isAllDay ?: fallback.isAllDay,
    createdOn = createdAt.orEmpty(),
    createdBy = createdById.orEmpty(),
    lastUpdatedOn = updatedAt.orEmpty(),
    lastUpdatedBy = updatedById.orEmpty()
)

private fun String.toDisplayDate(): String {
    val p = split("-")
    if (p.size != 3) return this
    val month = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec").getOrNull(p[1].toIntOrNull()?.minus(1) ?: -1) ?: return this
    return "${p[2].padStart(2, '0')} $month ${p[0]}"
}

private fun String.toDisplayTime(): String {
    val p = trim().removeSuffix("Z").split(":")
    return if (p.size == 3) "${p[0]}:${p[1]}" else this
}

private fun String.toApiDate(): String? {
    val p = trim().split(Regex("\\s+"))
    if (p.size != 3) return null
    val month = listOf("jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec").indexOf(p[1].lowercase()) + 1
    val day = p[0].toIntOrNull() ?: return null
    val year = p[2].toIntOrNull() ?: return null
    if (month !in 1..12 || day !in 1..31 || year !in 1900..2200) return null
    return "$year-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}"
}

private fun String.toApiTime(): String? = trim().takeIf { it.isNotBlank() && it != "--:--" }?.let { value ->
    val match = Regex("^(\\d{1,2}):(\\d{2})\\s*(AM|PM)$").matchEntire(value.uppercase()) ?: return@let value
    var hour = match.groupValues[1].toInt()
    val minute = match.groupValues[2]
    if (match.groupValues[3].uppercase() == "PM" && hour < 12) hour += 12
    if (match.groupValues[3].uppercase() == "AM" && hour == 12) hour = 0
    "${hour.toString().padStart(2, '0')}:$minute:00"
}
